import { toState } from '../redux/functions';
const JitsiConferenceErrors = JitsiMeetJS.errors.conference;
const JitsiConnectionErrors = JitsiMeetJS.errors.connection;
// getUserMedia 에 타임아웃(기본 5초)을 적용한다. 권한 요청 팝업이 방치되면 getUserMedia 가
// 영원히 pending 이라 장치연결 페이지 로딩 스피너가 안 풀리는 것을 막는다.
// ★유령 스트림 반납: 타임아웃으로 race 에서 패배한 getUserMedia 가 나중에(사용자가 뒤늦게
// '허용' 클릭) resolve 되면 아무도 stop 하지 않는 스트림이 카메라/마이크를 계속 점유
// (LED 점등, 이후 트랙 생성과 충돌 가능)하므로 즉시 정지시킨다.
const getUserMediaWithTimeout = (mediaConstraints, timeoutMs = 5000) => {
    let timedOut = false;
    const gumPromise = navigator.mediaDevices.getUserMedia(mediaConstraints);
    // timeoutMs <= 0: 기한 없이 대기 — 장치연결 "진입" 경로용. 권한 팝업 응답은 사용자의
    // 시간이므로 끊지 않아야 뒤늦은 [허용]에도 프리뷰가 정상 표시된다(스피너 해제·안내 문구
    // 전환은 UI 쪽 타이머가 담당). 장치 변경 등 권한이 이미 결정된 경로만 타임아웃을 쓴다.
    if (!timeoutMs || timeoutMs <= 0)
        return gumPromise;
    gumPromise
        .then(stream => {
        if (timedOut) {
            stream.getTracks().forEach(track => track.stop());
        }
    })
        .catch(() => { });
    return Promise.race([
        gumPromise,
        new Promise((_, reject) => setTimeout(() => {
            timedOut = true;
            const timeoutError = new Error('Operation timed out');
            timeoutError.name = 'TimeoutError';
            reject(timeoutError);
        }, timeoutMs))
    ]);
};
export const createWebRTCTracks = async (constraints = {}, timeoutMs = 5000) => {
    try {
        // 기본 constraints (카메라와 마이크 활성화)
        // 프리뷰는 1080으로 연다: 브라우저는 이미 열린 카메라의 캡처 해상도보다
        // 높은 해상도를 새 getUserMedia에 주지 못하므로(다운스케일만 가능),
        // 프리뷰가 720 이하로 열려 있으면 이후 회의 트랙의 1080 요청이 막힌다.
        const defaultConstraints = {
            video: { height: { ideal: 1080 } },
            audio: true // 마이크 트랙 활성화
        };
        // constraints 병합
        const mediaConstraints = { ...defaultConstraints, ...constraints };
        // 타임아웃을 적용한 getUserMedia 호출 (+유령 스트림 반납)
        const stream = await getUserMediaWithTimeout(mediaConstraints, timeoutMs);
        // 생성된 트랙들 추출
        const tracks = {
            videoTrack: stream.getVideoTracks()[0],
            audioTrack: stream.getAudioTracks()[0] // 첫 번째 오디오 트랙
        };
        return tracks;
    }
    catch (error) {
        console.error("Error creating WebRTC tracks:", error);
        throw error;
    }
};
export const createWebRTCVideoTrack = async (deviceId, constraints = {}, timeoutMs = 5000) => {
    try {
        // Default constraints for video
        // 프리뷰는 1080으로 연다 — createWebRTCTracks의 주석 참고 (카메라 해상도 선점 문제)
        const defaultConstraints = {
            video: {
                height: { ideal: 1080 },
                deviceId: {
                    exact: deviceId,
                } // Use the selected deviceId
            },
            audio: false // Disable audio track
        };
        // Merge default constraints with any custom constraints
        const mediaConstraints = { ...defaultConstraints, ...constraints };
        // Get user media — 타임아웃 적용(권한 팝업 방치 시 무한 pending → 스피너 고착 방지)
        const stream = await getUserMediaWithTimeout(mediaConstraints, timeoutMs);
        // Extract the first video track
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack) {
            throw new Error('No video track found');
        }
        return videoTrack;
    }
    catch (error) {
        console.error("Error creating WebRTC video track:", error);
        throw error;
    }
};
export const createWebRTCAudioTrack = async (deviceId, constraints = {}, timeoutMs = 5000) => {
    try {
        const defaultConstraints = {
            video: false,
            audio: {
                height: { ideal: 720 },
                deviceId: { exact: deviceId } // Use the selected deviceId
            } // Enable audio
        };
        const mediaConstraints = { ...defaultConstraints, ...constraints };
        // 타임아웃 적용(권한 팝업 방치 시 무한 pending → 스피너 고착 방지)
        const stream = await getUserMediaWithTimeout(mediaConstraints, timeoutMs);
        const audioTrack = stream.getAudioTracks()[0];
        if (!audioTrack) {
            throw new Error('No audio track found');
        }
        return audioTrack;
    }
    catch (error) {
        console.error('Error creating WebRTC audio track:', error);
        throw error;
    }
};
// lib-jitsi-meet DEFAULT_CONSTRAINTS.video 와 동일한 값. constraints 를 직접 넘기면
// lib 기본값이 통째로 대체되므로, constraints 미지정 호출에서도 동일 화질을 유지하기 위한 base.
const DEFAULT_VIDEO_CONSTRAINTS = {
    height: { ideal: 720, max: 720, min: 180 },
    width: { ideal: 1280, max: 1280, min: 320 },
};
/**
 * Creates a {@link JitsiLocalTrack} model from the given device id.
 *
 * @param {string} type - The media type of track being created. Expected values
 * are "video" or "audio".
 * @param {string} deviceId - The id of the target media source.
 * @param {number} [timeout] - A timeout for the JitsiMeetJS.createLocalTracks function call.
 * @param {Object} additionalOptions - Extra options to be passed to lib-jitsi-meet's {@code createLocalTracks}.
 *
 * @returns {Promise<JitsiLocalTrack>}
 */
export function createLocalTrack(type, deviceId, timeout, additionalOptions) {
    const baseOptions = {
        devices: [type],
        // eslint-disable-next-line camelcase
        // @ts-ignore
        firefox_fake_device: window.config?.firefox_fake_device,
        timeout,
        ...additionalOptions
    };
    // 비디오는 선택 장치를 deviceId:{exact} 로 고정한다. lib 는 cameraDeviceId 를 bare(ideal·
    // 비강제) 제약으로 넘기는데, 해상도 ideal(예: 1080)과 함께면 브라우저가 해상도를 더 잘
    // 만족하는 다른 카메라로 조용히 바꿔버린다(720p 카메라 선택 + 1080 요청에서 실증됨).
    // exact 실패 시에만 종전 bare 방식으로 재시도해 장치 유실(뽑힘 등) 상황을 그대로 흡수한다.
    if (type === 'video' && deviceId) {
        const constraints = additionalOptions?.constraints;
        const video = { ...(constraints?.video || DEFAULT_VIDEO_CONSTRAINTS), deviceId: { exact: deviceId } };
        return JitsiMeetJS.createLocalTracks({ ...baseOptions, constraints: { ...constraints, video } })
            .then(([jitsiLocalTrack]) => jitsiLocalTrack)
            .catch((err) => {
            console.warn(`선택 카메라(exact) 열기 실패 — 기본 선택으로 재시도: ${err?.name || err}`);
            return JitsiMeetJS.createLocalTracks({ ...baseOptions, cameraDeviceId: deviceId, micDeviceId: deviceId })
                .then(([jitsiLocalTrack]) => jitsiLocalTrack);
        });
    }
    return (JitsiMeetJS.createLocalTracks({
        ...baseOptions,
        cameraDeviceId: deviceId,
        micDeviceId: deviceId
    })
        .then(([jitsiLocalTrack]) => jitsiLocalTrack));
}
// @ts-ignore
export function createLocalTracks(devices, timeout, additionalOptions) {
    return (JitsiMeetJS.createLocalTracks({
        cameraDeviceId: null,
        devices,
        firefox_fake_device: 
        // @ts-ignore
        window.config && window.config.firefox_fake_device,
        micDeviceId: null,
        timeout,
        ...additionalOptions
    })
        .then((jitsiLocalTracks) => jitsiLocalTracks));
}
/**
 * Determines whether analytics is enabled in a specific redux {@code store}.
 *
 * @param {IStateful} stateful - The redux store, state, or
 * {@code getState} function.
 * @returns {boolean} If analytics is enabled, {@code true}; {@code false},
 * otherwise.
 */
export function isAnalyticsEnabled(stateful) {
    const { disableThirdPartyRequests, analytics = {} } = toState(stateful)['features/base/config'];
    return !(disableThirdPartyRequests || analytics.disabled);
}
/**
 * Determines whether a specific {@link JitsiConferenceErrors} instance
 * indicates a fatal {@link JitsiConference} error.
 *
 * FIXME Figure out the category of errors defined by the function and describe
 * that category. I've currently named the category fatal because it appears to
 * be used in the cases of unrecoverable errors that necessitate a reload.
 *
 * @param {Error|string} error - The {@code JitsiConferenceErrors} instance to
 * categorize/classify or an {@link Error}-like object.
 * @returns {boolean} If the specified {@code JitsiConferenceErrors} instance
 * indicates a fatal {@code JitsiConference} error, {@code true}; otherwise,
 * {@code false}.
 */
export function isFatalJitsiConferenceError(error) {
    if (typeof error !== 'string') {
        error = error.name; // eslint-disable-line no-param-reassign
    }
    return (error === JitsiConferenceErrors.FOCUS_DISCONNECTED
        || error === JitsiConferenceErrors.FOCUS_LEFT
        || error === JitsiConferenceErrors.ICE_FAILED
        || error === JitsiConferenceErrors.OFFER_ANSWER_FAILED
        || error === JitsiConferenceErrors.VIDEOBRIDGE_NOT_AVAILABLE);
}
/**
 * Determines whether a specific {@link JitsiConnectionErrors} instance
 * indicates a fatal {@link JitsiConnection} error.
 *
 * FIXME Figure out the category of errors defined by the function and describe
 * that category. I've currently named the category fatal because it appears to
 * be used in the cases of unrecoverable errors that necessitate a reload.
 *
 * @param {Error|string} error - The {@code JitsiConnectionErrors} instance to
 * categorize/classify or an {@link Error}-like object.
 * @returns {boolean} If the specified {@code JitsiConnectionErrors} instance
 * indicates a fatal {@code JitsiConnection} error, {@code true}; otherwise,
 * {@code false}.
 */
export function isFatalJitsiConnectionError(error) {
    if (typeof error !== 'string') {
        error = error.name; // eslint-disable-line no-param-reassign
    }
    return (error === JitsiConnectionErrors.CONNECTION_DROPPED_ERROR
        || error === JitsiConnectionErrors.OTHER_ERROR
        || error === JitsiConnectionErrors.SERVER_ERROR);
}
